import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { TicketRichEditor } from "../../components/editor/TicketRichEditor";
import { PageContentShell } from "../../components/layout/PageContentShell";
import { PageHeader } from "../../components/Page/PageHeader";
import { ticketService, type SimilarMatch, type TicketType } from "../../services/ticketService";
import { businessErrorCode } from "../../utils/apiError";
import { sanitizeTicketHtml } from "../../utils/sanitizeHtml";
import { isEmptyHtml, TICKET_MAX_BYTES, ticketPayloadBytes, utf8Bytes } from "../../utils/ticketPayload";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function logsHtml(logs: string): string {
  const trimmed = logs.trim();
  if (!trimmed) return "";
  return `<h3>Logs</h3><pre>${escapeHtml(trimmed)}</pre>`;
}

export function NewTicketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [type, setType] = useState<TicketType>("FEATURE");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [logs, setLogs] = useState("");
  const [error, setError] = useState("");
  const [similar, setSimilar] = useState<SimilarMatch[]>([]);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const issue = type === "BUG";
  const extra = issue ? utf8Bytes(logsHtml(logs)) : 0;
  const overLimit = ticketPayloadBytes(title, description) + extra > TICKET_MAX_BYTES;

  async function submit(confirmSimilar: boolean) {
    const html = sanitizeTicketHtml(description) + (issue ? logsHtml(logs) : "");
    if (!title.trim() || isEmptyHtml(html)) {
      setError(t("tickets.required"));
      return;
    }
    if (ticketPayloadBytes(title, html) > TICKET_MAX_BYTES) {
      setError(t("tickets.tooLarge"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await ticketService.create({ type, title: title.trim(), description: html, confirmSimilar });
      if (result.requiresConfirmation) {
        setNeedsConfirm(true);
        setSimilar(result.similar || []);
        return;
      }
      if (result.ticket) {
        navigate(`/tickets/${result.ticket.id}`);
      }
    } catch (err) {
      const code = businessErrorCode(err);
      const data = (err as { response?: { data?: { response?: { similar?: SimilarMatch[] } } } })?.response?.data;
      const matches = data?.response?.similar;
      if (code === "SIMILAR_TICKET_BLOCKED") {
        setError(t("tickets.blocked"));
        setSimilar(matches || []);
      } else if (code === "PACKAGE_QUOTA_EXCEEDED") {
        setError(t("tickets.quotaExceeded"));
      } else if (code === "TICKET_PAYLOAD_TOO_LARGE") {
        setError(t("tickets.tooLarge"));
      } else {
        setError(t("tickets.createFailed"));
      }
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit(needsConfirm);
  }

  return (
    <PageContentShell>
      <PageHeader title={t("tickets.new")} subtitle={t("tickets.newSubtitle")} />
      <Alert severity="info" sx={{ mb: 2 }}>
        {t("tickets.scopeHint")} {t("tickets.writeHint")}
      </Alert>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {needsConfirm ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("tickets.confirmSimilar")}
        </Alert>
      ) : null}
      {similar.length > 0 ? (
        <Paper sx={{ mb: 2 }}>
          <List>
            {similar.map((item) => (
              <ListItem key={item.id} component={Link} to={`/tickets/${item.id}`}>
                <ListItemText primary={`${item.number} · ${item.title}`} secondary={`score ${item.score}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      ) : null}
      <Paper sx={{ p: 2.5, maxWidth: 860 }}>
        <Stack component="form" onSubmit={onSubmit} spacing={2}>
          <FormControl>
            <InputLabel>{t("tickets.type")}</InputLabel>
            <Select value={type} label={t("tickets.type")} onChange={(e) => setType(e.target.value as TicketType)}>
              <MenuItem value="SUPPORT">{t("types.SUPPORT")}</MenuItem>
              <MenuItem value="FEATURE">{t("types.FEATURE")}</MenuItem>
              <MenuItem value="CHANGE">{t("types.CHANGE")}</MenuItem>
              <MenuItem value="BUG">{t("types.BUG")}</MenuItem>
            </Select>
          </FormControl>
          {issue ? (
            <Alert severity="info">{t("tickets.issueHint")}</Alert>
          ) : null}
          <TextField label={t("tickets.titleField")} value={title} onChange={(e) => setTitle(e.target.value)} required />
          <TicketRichEditor
            title={title}
            html={description}
            onHtml={setDescription}
            extraBytes={extra}
            placeholder={issue ? t("editor.issuePlaceholder") : t("editor.placeholder")}
          />
          {issue ? (
            <TextField
              label={t("tickets.logs")}
              placeholder={t("tickets.logsPlaceholder")}
              value={logs}
              onChange={(e) => setLogs(e.target.value)}
              multiline
              minRows={5}
            />
          ) : null}
          <Button type="submit" variant="contained" disabled={busy || overLimit}>
            {needsConfirm ? t("tickets.confirmSimilar") : t("tickets.create")}
          </Button>
        </Stack>
      </Paper>
    </PageContentShell>
  );
}
